import React from "react";
import { useSelector } from "react-redux";
import Utils from "../../Utils/utils";
import Spinner from "../UI/Spinner/Spinner";
import classes from "./ServicesApp.module.scss";
import { pt } from "../../Utils/panelText";

const ServicesApp = ({ title, imageUrl, onClick, open = false, isLoading = false, mode = 'ai' }) => {
    const { chatSettingsData } = useSelector((state) => state.chatServicesData);
    const { messages = [] } = useSelector((state) => state.chatSocket || {});

    // Determine avatar source based on mode
    const getAvatarSrc = () => {
        if (mode === 'ai') {
            // Use bot image from dashboard settings (type 400) or customer service (type 332)
            const botImage = chatSettingsData?.images?.find(img => img?.type === 400)?.url;
            const customerServiceImage = chatSettingsData?.images?.find(img => img?.type === 332)?.url;
            const imageToUse = botImage || customerServiceImage;
            return imageToUse ? Utils.getProperImageUrl(imageToUse) : '/images/ai-avatar.png';
        }

        if (mode === 'human' || mode === 'team') {
            // Try to get the most recent agent's image from Redux messages
            const recentAgentMessages = messages
                .filter(m => (m.senderType === 'AGENT' || m.senderType === 'agent') && m.sender?.image)
                .slice(-1);

            if (recentAgentMessages.length > 0 && recentAgentMessages[0].sender?.image) {
                return Utils.getProperImageUrl(recentAgentMessages[0].sender.image);
            }

            return imageUrl ? Utils.getProperImageUrl(imageUrl) : '/images/support-avatar.png';
        }

        return '/images/support-avatar.png'; // Default fallback
    };

    // Get team avatars for Team Mode
    const getTeamAvatars = () => {
        const recentAgentMessages = messages
            .filter(m => (m.senderType === 'AGENT' || m.senderType === 'agent') && m.sender?.id)
            .slice(-10);

        // Get unique agents
        const uniqueAgents = [];
        const seenIds = new Set();

        for (const msg of recentAgentMessages.reverse()) {
            if (msg.sender?.id && !seenIds.has(msg.sender.id)) {
                seenIds.add(msg.sender.id);
                uniqueAgents.push({
                    id: msg.sender.id,
                    image: msg.sender.image || null,
                    name: msg.sender.name || 'Agent'
                });
                if (uniqueAgents.length >= 2) break; // Only show first 2
            }
        }

        return uniqueAgents;
    };

    // Determine launcher title based on mode
    const getLauncherTitle = () => {
        if (mode === 'ai') return pt('aiAssistant');
        if (mode === 'team') return pt('ourTeam');
        return title; // Human mode uses default title
    };

    return (
        <button
            onClick={onClick}
            disabled={isLoading}
            className={classes.serviceAppIcon}
        >
            {isLoading ? (
                <Spinner />
            ) : (
                <>
                    {!open && (
                        <div className={classes.falgHelpMsg}>
                            <p className={classes.helpMsgText}>{pt("hereToHelp")}</p>
                            <span className={classes.trangle} />
                        </div>
                    )}

                    <div className={`${classes.orgContent} ${mode === 'ai' ? classes.aiMode : ''} ${mode === 'team' ? classes.teamMode : ''}`}>
                        <div className={classes.backDrop} />

                        {mode === 'team' ? (
                            // Team Mode: Show stacked avatars
                            <div className={classes.teamAvatars}>
                                {getTeamAvatars().map((agent, index) => (
                                    <div key={agent.id} className={classes.stackedAvatar} style={{ zIndex: 10 - index }}>
                                        <img
                                            src={agent.image ? Utils.getProperImageUrl(agent.image) : '/images/support-avatar.png'}
                                            alt={agent.name}
                                        />
                                    </div>
                                ))}
                                <span className={classes.statusDot} />
                            </div>
                        ) : (
                            // AI/Human Mode: Show single avatar
                            <div className={classes.logoWrapper}>
                                <img
                                    src={getAvatarSrc()}
                                    alt={getLauncherTitle()}
                                />
                                {mode !== 'ai' && <span className={classes.statusDot} />}
                            </div>
                        )}

                        <p className={classes.orgTitle}>{getLauncherTitle()}</p>
                    </div>
                </>
            )}
        </button>
    );
};

export default ServicesApp;
